import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import PageNavigationContext from './PageNavigationContext';

import { ColorName, Colors } from '../../../theme';
import { Icon } from '@zapier/zinnia-icons';
import { Text } from '../../display/Text';
import { Tooltip, TooltipFocusableTrigger } from '../../display/Tooltip';
import { Spacer } from '../../layout/Spacer';

import NestedSecondRow from './NestedSecondRow';
import PageNavigationBadge from './PageNavigationBadge';
import PageNavigationRow from './PageNavigationRow';
import type { ContentProps, OnSelectType, PageNavigationItem } from './types';
import checkIsTruncated from './utils/checkIsTruncated';

import styles from './NestedFirstRow.module.scss';

/**
 * NestedFirstRow is a row renderer component that renders its item
 * and any children, if provided, as a nested list via the NestedSecondRow
 * component.
 */
export default function NestedFirstRow({
  isExpanded,
  item,
  onSelect,
}: {
  item: PageNavigationItem;
  isExpanded: boolean;
  onSelect?: OnSelectType;
}) {
  const { toggleExpand } = useContext(PageNavigationContext);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLElement>(null);

  const nestedListRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    // React doesn't support the `until-found` value for the `hidden` attribute, so we set it directly on the ul with a ref.
    if (nestedListRef.current) {
      if (isExpanded) {
        nestedListRef.current.removeAttribute('hidden');
      } else {
        nestedListRef.current.setAttribute('hidden', 'until-found');
      }
    }
  }, [isExpanded]);

  useEffect(() => {
    setIsTruncated(checkIsTruncated(textRef));
  }, []);

  const onToggleSelect = () => {
    toggleExpand(item.id);
    return onSelect;
  };

  const InnerContent = ({ hover, selected }: ContentProps) => {
    const textColor: ColorName =
      hover || selected ? 'UiPrimaryStrongest' : 'GrayWarm10';

    return (
      <div
        className={styles.rowContainer}
        data-testid="pagenavigation-nestedfirstrow"
        data-zds
      >
        <span className={styles.textWrap}>
          {item.children ? (
            <div
              className={styles.iconContainer}
              data-testid="pagenavigation-nestedfirstrow-icon"
              data-zds
            >
              <Icon
                color="GrayWarm6"
                name={isExpanded ? 'arrowSmallUp' : 'arrowSmallDown'}
                size={20}
              />
            </div>
          ) : (
            <Spacer width={20} />
          )}
          <span
            className={clsx(
              styles.textEllipsis,
              item.children && styles.iconTextSpacing
            )}
            style={{ color: Colors[textColor] }}
            data-testid="pagenavigation-nestedfirstrow-text"
            ref={textRef}
            data-zds
          >
            <Text color={textColor} margin="0 0 0 8px" type="Body3">
              {item.title}
            </Text>
          </span>
        </span>
        {item.count && (
          <PageNavigationBadge variant="neutral">
            {item.count.toString()}
          </PageNavigationBadge>
        )}
      </div>
    );
  };

  const Content = ({ hover, selected }: ContentProps) =>
    isTruncated ? (
      <Tooltip allowMultiline content={item.title} placement="top right">
        <TooltipFocusableTrigger>
          <InnerContent hover={hover} selected={selected} />
        </TooltipFocusableTrigger>
      </Tooltip>
    ) : (
      <InnerContent hover={hover} selected={selected} />
    );

  const NestedList = useMemo(
    () =>
      item.children
        ? () => (
            <ul
              className={styles.list}
              data-zds
              hidden={!isExpanded}
              ref={nestedListRef}
            >
              {item.children?.map((i) => (
                <NestedSecondRow item={i} key={i.id} onSelect={onSelect} />
              ))}
            </ul>
          )
        : undefined,
    [item, isExpanded, onSelect]
  );

  return (
    <PageNavigationRow
      content={Content}
      isSelectable={false}
      item={item}
      nestedList={NestedList}
      onSelect={item.children ? onToggleSelect : onSelect}
    />
  );
}
